import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import allPhotoStoryPosts from "../data/photoStoryPosts";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./PhotoStoryPost.css";

const photoStoryPosts = allPhotoStoryPosts.filter(
  (post) => post.brand !== "Cloudy Pixel Studio"
);

const toAssetUrl = (path) => {
  if (!path || typeof path !== "string") return "/images/logo192.png";
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) return path;
  if (path.startsWith("/uploads") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }
  return path;
};

const getField = (item, names, fallback = "") => {
  for (const name of names) {
    if (item && item[name] !== undefined && item[name] !== null && item[name] !== "") {
      return item[name];
    }
  }
  return fallback;
};

const toKebab = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const setOrCreateMeta = (selector, attrName, attrValue, content) => {
  let meta = document.querySelector(selector);

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attrName, attrValue);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
};

const PhotoStoryPost = () => {
  const { slug } = useParams();

  const post = useMemo(
    () => photoStoryPosts.find((item) => item.slug === slug),
    [slug]
  );

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    if (!post) {
      document.title = "Story Not Found | MayaChitro";
      return;
    }

    const pageTitle = post.seoTitle || `${post.title} | MayaChitro`;
    const description = post.metaDescription || post.excerpt || post.title;
    const imageUrl = toAssetUrl(post.image);

    document.title = pageTitle;

    setOrCreateMeta('meta[name="description"]', "name", "description", description);
    setOrCreateMeta('meta[property="og:title"]', "property", "og:title", pageTitle);
    setOrCreateMeta('meta[property="og:description"]', "property", "og:description", description);
    setOrCreateMeta('meta[property="og:image"]', "property", "og:image", imageUrl);
    setOrCreateMeta('meta[property="og:type"]', "property", "og:type", "article");
    setOrCreateMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
  }, [post]);

  useEffect(() => {
    let ignore = false;

    async function fetchProducts() {
      try {
        setLoadingProducts(true);

        const response = await fetch(buildApiUrl("/products?status=published"), {
          headers: { Accept: "application/json" },
        });

        const data = await response.json();

        if (!ignore && response.ok && data.success) {
          setProducts(data.products || []);
        }
      } catch (error) {
        console.error("Failed to load products for photo story:", error);
      } finally {
        if (!ignore) {
          setLoadingProducts(false);
        }
      }
    }

    fetchProducts();

    return () => {
      ignore = true;
    };
  }, []);

  const mainProduct = useMemo(() => {
    if (!post) return null;

    return (
      products.find((product) => product.slug === post.productSlug) ||
      products.find((product) => product.productId === post.productId) ||
      null
    );
  }, [post, products]);

  const relatedProducts = useMemo(() => {
    if (!post) return [];

    const postTags = post.tags || [];
    const postBrand = post.productBrandSlug || "";

    const scoredProducts = products
      .filter((product) => product.slug !== post.productSlug)
      .map((product) => {
        const productBrand = getField(product, ["brandSlug", "brand_slug"], "");
        const searchable = [
          getField(product, ["title"]),
          getField(product, ["slug"]),
          getField(product, ["category"]),
          getField(product, ["categorySlug", "category_slug"]),
          getField(product, ["collection", "collectionSlug", "collection_slug"]),
          getField(product, ["primaryKeyword", "primary_keyword"]),
        ]
          .join(" ")
          .toLowerCase();

        let score = 0;

        if (productBrand === postBrand) score += 5;

        postTags.forEach((tag) => {
          const firstWord = String(tag).toLowerCase().split(" ")[0];
          if (firstWord && searchable.includes(firstWord)) score += 2;
        });

        if (
          post.productCategory &&
          searchable.includes(String(post.productCategory).toLowerCase().split(" ")[0])
        ) {
          score += 3;
        }

        return { product, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.product);

    const backupProducts = products.filter(
      (product) =>
        product.slug !== post.productSlug &&
        getField(product, ["brandSlug", "brand_slug"], "") === postBrand
    );

    const unique = [];

    [...scoredProducts, ...backupProducts].forEach((product) => {
      const key = product.id || product.productId || product.slug;

      if (!unique.some((item) => (item.id || item.productId || item.slug) === key)) {
        unique.push(product);
      }
    });

    return unique.slice(0, 4);
  }, [post, products]);

  const relatedStories = useMemo(() => {
    if (!post) return [];

    return photoStoryPosts
      .filter((item) => item.slug !== post.slug)
      .filter(
        (item) =>
          item.category === post.category ||
          item.brand === post.brand ||
          (item.tags || []).some((tag) => (post.tags || []).includes(tag))
      )
      .slice(0, 3);
  }, [post]);

  if (!post) {
    return (
      <main className="photo-story-post-page not-found">
        <div className="story-not-found-card">
          <p className="story-eyebrow">Photo Stories</p>
          <h1>Story not found</h1>
          <p>The story may have been moved, renamed, or removed.</p>
          <Link to="/stories" className="story-btn story-btn-gold">
            Back to Photo Stories
          </Link>
        </div>
      </main>
    );
  }

  const sections = Array.isArray(post.sections) ? post.sections : [];
  const faqs = Array.isArray(post.faqs) ? post.faqs : [];
  const tags = Array.isArray(post.tags) ? post.tags : [];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: toAssetUrl(post.image),
    author: {
      "@type": "Organization",
      name: "MayaChitro",
    },
    publisher: {
      "@type": "Organization",
      name: "MayaChitro",
    },
    datePublished: post.date || undefined,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `/stories/${post.slug}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Photo Stories",
        item: "/stories",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: `/stories/${post.slug}`,
      },
    ],
  };

  return (
    <main className="photo-story-post-page">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <section className="story-post-hero">
        <div className="story-post-hero-copy">
          <Link to="/stories" className="story-back-link">
            ← Photo Stories
          </Link>

          <p className="story-eyebrow">{post.category || "Photo Story"}</p>

          <h1>{post.title}</h1>

          <p className="story-post-intro">{post.excerpt}</p>

          <div className="story-post-meta">
            <span>{post.brand || "MayaChitro"}</span>
            <span>{post.date || "MayaChitro Journal"}</span>
            <span>{post.readingTime || "3 min read"}</span>
          </div>

          <div className="story-post-actions">
            {mainProduct ? (
              <Link to={`/product/${mainProduct.slug}`} className="story-btn story-btn-gold">
                {post.productCta || "View Product"}
              </Link>
            ) : (
              <Link
                to={`/shop?brand=${post.productBrandSlug || "mayachitro"}`}
                className="story-btn story-btn-gold"
              >
                Browse Related Products
              </Link>
            )}

            <a href="#story-content" className="story-btn story-btn-outline">
              Read Story
            </a>
          </div>
        </div>

        <div className="story-post-hero-image">
          <img src={toAssetUrl(post.image)} alt={post.imageAlt || post.title} />
          <div className="story-image-caption">
            <span>{post.type || "Photo Story"}</span>
            <strong>{post.productCategory || post.category || "Visual Story"}</strong>
          </div>
        </div>
      </section>

      <section className="story-post-layout" id="story-content">
        <article className="story-post-article">
          <p className="story-post-lead">{post.intro}</p>

          {sections.length > 0 && (
            <nav className="story-toc" aria-label="Story sections">
              <p className="story-eyebrow">In this story</p>
              <div>
                {sections.map((section) => (
                  <a href={`#${toKebab(section.heading)}`} key={section.heading}>
                    {section.heading}
                  </a>
                ))}
                <a href="#buying-guide">Buying Guide</a>
                {faqs.length > 0 && <a href="#story-faq">FAQ</a>}
              </div>
            </nav>
          )}

          {sections.map((section) => (
            <section
              className="story-content-section"
              id={toKebab(section.heading)}
              key={section.heading}
            >
              <h2>{section.heading}</h2>
              <p>{section.body}</p>
            </section>
          ))}

          <section className="story-buying-guide" id="buying-guide">
            <p className="story-eyebrow">Buying Guide</p>
            <h2>How to Use or License This Visual</h2>
            <p>{post.buyingGuide}</p>

            <div className="story-inline-cta">
              <div>
                <strong>Interested in this visual?</strong>
                <span>
                  Open the product page to check price, license options, file details,
                  and instant download information.
                </span>
              </div>

              {mainProduct ? (
                <Link to={`/product/${mainProduct.slug}`} className="story-btn story-btn-gold">
                  {post.productCta || "View Product"}
                </Link>
              ) : (
                <Link
                  to={`/shop?brand=${post.productBrandSlug || "mayachitro"}`}
                  className="story-btn story-btn-gold"
                >
                  Browse Products
                </Link>
              )}
            </div>
          </section>

          {faqs.length > 0 && (
            <section className="story-faq" id="story-faq">
              <p className="story-eyebrow">FAQ</p>
              <h2>Common Questions</h2>

              {faqs.map((item) => (
                <details key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </section>
          )}
        </article>

        <aside className="story-sidebar">
          <div className="story-product-box">
            <p className="story-eyebrow">Shop This Story</p>

            {loadingProducts && <p className="story-loading">Loading product...</p>}

            {!loadingProducts && mainProduct && (
              <ProductCard product={mainProduct} compact />
            )}

            {!loadingProducts && !mainProduct && (
              <div className="story-product-fallback">
                <h3>{post.productCta || "View Related Products"}</h3>
                <p>
                  Product connection will appear here when the matching product slug exists
                  in your database.
                </p>
                <Link
                  to={`/shop?brand=${post.productBrandSlug || "mayachitro"}`}
                  className="story-btn story-btn-outline"
                >
                  Browse Shop
                </Link>
              </div>
            )}
          </div>

          <div className="story-tags-box">
            <p className="story-eyebrow">Story Tags</p>
            <div className="story-tags">
              {tags.map((tag) => (
                <Link to={`/stories?category=${encodeURIComponent(post.category)}`} key={tag}>
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="story-product-box">
            <p className="story-eyebrow">Next Step</p>
            <h3>Explore More Visuals</h3>
            <p>
              Browse related MayaChitro photographs and licensed visual products.
            </p>
            <Link to="/shop" className="story-btn story-btn-gold">
              Visit Shop
            </Link>
          </div>
        </aside>
      </section>

      {relatedStories.length > 0 && (
        <section className="story-related-stories-section">
          <div className="story-section-heading">
            <div>
              <p className="story-eyebrow">Related Stories</p>
              <h2>Read More from the Journal</h2>
            </div>
            <Link to="/stories">All Stories →</Link>
          </div>

          <div className="related-story-grid">
            {relatedStories.map((story) => (
              <Link to={`/stories/${story.slug}`} className="related-story-card" key={story.slug}>
                <img src={toAssetUrl(story.image)} alt={story.imageAlt || story.title} />
                <div>
                  <span>{story.category}</span>
                  <h3>{story.title}</h3>
                  <p>{story.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="story-related-section">
        <div className="story-section-heading">
          <div>
            <p className="story-eyebrow">Related Products</p>
            <h2>More Visuals You May Like</h2>
          </div>

          <Link to={`/shop?brand=${post.productBrandSlug || "mayachitro"}`}>
            View More →
          </Link>
        </div>

        {relatedProducts.length > 0 ? (
          <div className="story-related-grid">
            {relatedProducts.map((product) => (
              <ProductCard
                product={product}
                compact
                key={product.id || product.productId || product.slug}
              />
            ))}
          </div>
        ) : (
          <div className="story-empty-related">
            Related products will appear here after more matching products are added.
          </div>
        )}
      </section>
    </main>
  );
};

export default PhotoStoryPost;
